// Basic
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
// Libs
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
// Components
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import FooterForm from '@/common-utils/components/renders/modal/components/footers/FooterForm/FooterForm';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
// Lang
import { nsBeneficiaries } from '@/app/i18n/namespaces';
import Entity from '@/common-utils/components/renders/entity/Entity';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.beneficiary
 * @param root0.onSubmit
 * @param root0.isSubmitting
 * @returns {JSX} ModalUpdateBeneficiaryLabel
 */
function ModalUpdateBeneficiaryLabel({ isOpen, setIsOpen, beneficiary, onSubmit, isSubmitting }) {
    // Form
    const formSchema = z.object({
        label: z.string()
            .nonempty()
            .min(1, '') // At least 1 character
            .max(100, '') // Max 100 characters
    });
    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: { label: beneficiary?.name || '' }
    });

    // State
    const [showSubmitButton, setShowSubmitButton] = useState(true);

    // Functions
    const handleSubmit = (data) => {
        setShowSubmitButton(false);
        onSubmit(data.label);
    };
    const onClose = () => {
        setIsOpen(false);
    };

    return (
        <Modal open={isOpen} onOpenChange={onClose}>
            <ModalContent className='max-w-lg'>
                <HeaderTitle title='modal.update_label.title' namespace={nsBeneficiaries} />
                <ModalBody>
                    <Form {...form} onSubmit={form.handleSubmit(handleSubmit)}>
                        <FormLabel htmlFor='label' className='block text-sm font-medium text-gray-700 mb-2'>
                            <Entity entity='modal.update_label.label' namespace={nsBeneficiaries} />
                        </FormLabel>
                        <FormField
                            name='label'
                            control={form.control}
                            render={({ field }) => (
                                <FormItem className='addresses-list-input'>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            autoFocus
                                            type='text'
                                            placeholder={beneficiary?.name}
                                            autoComplete='off'
                                            value={field.value}
                                            onChange={(e) => field.onChange(e.target.value)}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </Form>
                </ModalBody>
                <FooterForm
                    onSubmit={form.handleSubmit(handleSubmit)}
                    isSubmitting={isSubmitting}
                    showSubmitButton={showSubmitButton}
                    isSubmitDisabled={!form.formState.isValid}
                />
            </ModalContent>
        </Modal>
    );
}

ModalUpdateBeneficiaryLabel.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    beneficiary: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string
    }).isRequired,
    onSubmit: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool
};

export default ModalUpdateBeneficiaryLabel;
